import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export interface NavigationStyleOption {
  name: string;
  value: string;
}

@Component({
  selector: 'vb-navigation-style-selector',
  templateUrl: './navigation-style-selector.component.html',
  styleUrls: ['./navigation-style-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NavigationStyleSelectorComponent),
      multi: true
    }
  ]
})
export class NavigationStyleSelectorComponent implements ControlValueAccessor {
  private readonly cd = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  @Input() options: NavigationStyleOption[] = [];
  @Input() disabled = false;

  value: string = '';
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  readonly styleConfig: Record<string, { icon: string; titleKey: string; descriptionKey: string }> = {
    type1: {
      icon: 'assets/images/svg/icon-standard-menu.png',
      titleKey: 'NAV_STYLE_STANDARD_MENU',
      descriptionKey: 'NAV_STYLE_STANDARD_MENU_DESC'
    },
    type2: {
      icon: 'assets/images/svg/icon-multi-level-menu.png',
      titleKey: 'NAV_STYLE_MULTI_LEVEL_MENU',
      descriptionKey: 'NAV_STYLE_MULTI_LEVEL_MENU_DESC'
    }
  };

  writeValue(value: string): void {
    this.value = value || '';
    this.cd.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  selectOption(optionValue: string): void {
    if (this.disabled) return;
    
    this.value = optionValue;
    this.onChange(optionValue);
    this.onTouched();
    this.cd.markForCheck();
  }

  getConfig(optionValue: string) {
    return this.styleConfig[optionValue] || null;
  }
}
